/* eslint-disable @typescript-eslint/camelcase */
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { getAppEnv } from '../../../config/env';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import PageHeader from '../../components/PageHeader/PageHeader';
import { EnvironmentVariables } from '../../types';
import {
  AirportHomePageData,
  AirportHomePageTemplateDataChecker
} from './AirportHomePageDataChecker';
import { useMediaQuery } from 'react-responsive';
import './AirportHomePage.css';
import Footer from '../../components/Footer/Footer';
import FaqSection from '../../components/Faq/Faq';
import CompactList from '../../components/CompactList/CompactList';
import '../CityOperatorTemplate/CityOperatorTemplate.css';
import AdsComponent from '../../components/adsComponent';
import '../../components/ReadMore/ReadMore.css';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import { PathReporter } from 'io-ts/lib/PathReporter';
import AppContextProvider from '../../AppContext';
import { trackAirportHomePageCabPageEvent } from '../../utils/omnitureTracking';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import '../../components/TopCabsRoutes/TopCabsRoutes.css';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import { SEO_AIRPORT_HOME_PAGE } from '../../constants/templateConstants';
import { useSearchWidgetHooks } from '../../utils/commonUtils';
const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

type AirportHomePageDataProp = {
  data: AirportHomePageData;
};

const runtimeTypChecker = (data: AirportHomePageData) => {
  const result = AirportHomePageTemplateDataChecker.decode(data);
  if (result._tag !== 'Right') {
    // console.log(PathReporter.report(result));
    ThrowReporter.report(result);
  }
};

const AirportHomePage = ({ data }: AirportHomePageDataProp) => {
  runtimeTypChecker(data);
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });

  const { templateData, cabTypesAvailableForRent, hrPackages } = data;

  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_AIRPORT_HOME_PAGE
  );

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_AIRPORT_HOME_PAGE}`}
      trackTemplateEvent={trackAirportHomePageCabPageEvent}
    >
      <div className={`cabSeoDtPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className="infoForCrawler">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader
            title={templateData.h1Title}
            extraInfo={templateData.extraInfo}
            isMobileDevice={isMobileDevice}
            isExtraInfoVisible={true}
          />
        </div>
        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            isMobileDevice={true}
            title={`Outstation Cabs`}
            omniString={`_Airport_Home_Page`}
            hrPackages={hrPackages}
          />
        </div>

        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            isMobileDevice={false}
            title={`Outstation Cabs`}
            omniString={`_Airport_Home_Page`}
            hrPackages={hrPackages}
          />
        </div>
        <main className="container">
          <div style={{ display: 'none' }} className={'infoForUser'}>
            <BreadCrumb breadcrumb={templateData.breadcrumb} />
            <PageHeader
              title={templateData.h1Title}
              extraInfo={templateData.extraInfo}
              isMobileDevice={isMobileDevice}
              isExtraInfoVisible={true}
            />
          </div>
          <div className="appendBottom20"></div>
          <RentalCabs
            h2={`Airport Taxi Prices`}
            cityName={''}
            data={cabTypesAvailableForRent}
          />
          <div
            style={{ flexWrap: 'wrap' }}
            className={`cabsSeoWrap__Details makeFlex`}
          >
            {data.routesFromAirportCities.map((itemM, idx) => (
              <CompactList
                titlePrefix={`Cabs from `}
                titleSuffix={itemM.cityName}
                list={itemM.routeUrlsList}
                type={'bullets'}
                className={'rightMargin'}
              >
                {(item: any, idx, style) => (
                  <li
                    key={`${item.sourceCityName}_${idx}`}
                    className={`${
                      idx ? `paddingT10 paddingB10` : `paddingB10`
                    }`}
                  >
                    {item.routeUrl ? (
                      <a
                        className={`linkBoxFont`}
                        onClick={event => {
                          event.preventDefault();
                          trackAirportHomePageCabPageEvent(
                            `Cab_SEO_Airport_Home_Page_Route_Link_Click`
                          );
                          window.location = Object(item.url);
                        }}
                        href={item.routeUrl}
                      >
                        {`${item.sourceCityName} to ${item.destinationCityName} Cabs`}
                      </a>
                    ) : null}
                  </li>
                )}
              </CompactList>
            ))}
            {data.primaryAirportList && data.primaryAirportList.length > 0 && (
              <CompactList
                titlePrefix={`Our Airport Cabs  `}
                titleSuffix={``}
                list={data.primaryAirportList}
                type={'bullets'}
                className={'rightMargin'}
              >
                {(item: any, idx, style) => (
                  <li
                    key={`${item.name}_${idx}`}
                    className={`${
                      idx ? `paddingT10 paddingB10` : `paddingB10`
                    }`}
                  >
                    {item.url ? (
                      <a
                        className={`linkBoxFont`}
                        onClick={() => {
                          trackAirportHomePageCabPageEvent(
                            `Cab_SEO_Airport_Home_Page_Airport_Link_Click`
                          );
                        }}
                        href={item.url}
                      >
                        {`${item.name} Airport Taxi`}
                      </a>
                    ) : (
                      <span>{`${item.name} Airport Taxi`}</span>
                    )}
                  </li>
                )}
              </CompactList>
            )}
          </div>
          {/* {templateData &&
            templateData?.readMore &&
            templateData?.readMore.length > 0 && (
              <div
                style={{ display: 'none' }}
                className="paddingB20 infoForUser"
              >
                <ReadMore data={templateData.readMore} />
              </div>
            )} */}
          {templateData && templateData.descriptionInHtml ? (
            <section id="descriptionInHtml" className="visibilityAuto">
              <ThingsToKnow
                html={templateData?.descriptionInHtml}
              ></ThingsToKnow>
            </section>
          ) : null}
          <section>
            <h2>{`FAQs on Airport taxi Service`}</h2>
            <FaqSection
              trackTemplateEvent={trackAirportHomePageCabPageEvent}
              data={templateData.faq}
              pageUrl={templateData.sampleUrl}
            />
          </section>
        </main>
        <>
          <Footer template={data.templateData} />
        </>
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

AirportHomePage.fetchData = async (_, match, urlObj, gRPCClient) => {
  const urlString = `${DATA_LOADER_URL}/api/getAirportHomeData`;

  let responseData;
  const response = await fetch(urlString);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new DataLoaderError(`Unable to fetch bus list`, responseData.error);
  }

  const pathArray = urlObj.path.split('/');
  const cacheKey =
    'HTML_STATIC_' + pathArray[pathArray.length - 1].toUpperCase();

  // needs to be  in  a separate file or done differently,
  const templateData = {
    templateName: 'static-airport-taxi',
    metaTitle: 'Airport Taxi : Book Airport cabs online at lowest fare',
    metaDescription:
      'Book airport taxi services at lowest price and make your Journey safe & hassle free experience with MakeMyTrip. Choose from a range of sedans,SUVs and some of the car options include Dzire, Eritga, Innova and more.',
    metaKeywords:
      'airport taxi,airport cab, airport taxi booking, airport taxi service, airport cab service,airport cab booking',
    analytics: `var s=s_gi('mmtprod');
              s.pageName="seo:domestic cabs:Airport taxi";
              s.channel="seotemplate domestic cabs funnel";
              s.server = window.location.hostname;
              s.prop24="domestic cabs";
              if(typeof(mboxLoadSCPlugin) != "undefined" ){
              mboxLoadSCPlugin(s);
              }
              var s_code=s.t();if(s_code)document.write(s_code);`,
    h1Title: 'Book Airport Taxi',
    extraInfo: '',
    bannerMobile: 'top',
    bannerDesktop: 'top',
    breadcrumb: [
      {
        displayString: 'Home',
        redirectionUrl: 'https://www.makemytrip.com/'
      },
      {
        displayString: 'Cabs',
        redirectionUrl: 'https://www.makemytrip.com/cabs/'
      },
      {
        displayString: ' Airport Cab',
        redirectionUrl:
          'https://www.makemytrip.com/car-rental/airport-taxi.html'
      }
    ],
    faq: [
      {
        question: `What are the advantages of online airport cab booking from Makemytrip?`,
        answer: `Online cab booking not only helps you with best prices but also helps you with the convenience of paying through multiple payment options (like Debit Card, Credit Card, eWallets etc.).`
      },
      {
        question: `Which is the most economical cab available for airport cab booking?`,
        answer: `The most economical cab available in for airport transfers is HATCHBACK.`
      },
      {
        question: `Which online payment methods are accepted on MakeMyTrip for cabs booking?`,
        answer: `On MakeMyTrip, you can book an airport taxi and make online payments through various methods including Credit Cards, Debit Cards, UPI Transactions and Net Banking`
      },
      {
        question: `When would I receive the information related to my driver?`,
        answer: `Once you have booked your airport taxi, you will receive the details of your driver about 30 minutes before the departure.`
      },
      {
        question: `Is there any way to know the location of my airport taxi?`,
        answer: `Yes, you can know the location of your airport taxi by making use of the ‘Live GPS Tracking’ feature on MakeMyTrip.`
      },
      {
        question: `Can I make a partial payment for booking my airport taxi?`,
        answer: `Yes, MakeMyTrip gives you the option of either making the full payment in one go or reserving your taxi by making partial payment and giving the rest of the amount to the driver once you have covered the journey.`
      },
      {
        question: `Do I have to carry any ID proof to avail of cab services on MakeMyTrip?`,
        answer: `Yes, passengers are requested to carry and show a photo ID while boarding their cab to help the driver in identifying`
      }
    ],
    externalJs: '',
    sampleUrl: 'https://www.makemytrip.com/car-rental/airport-taxi.html',
    footerData: responseData.footerData,
    descriptionInHtml: `<h2>Book Airport Taxi Online at the Cheapest Price</h2>
	<p>It&rsquo;s a well-known fact that usually, airports are the busiest destinations. When you&rsquo;re in a hurry to catch an important flight, booking an airport taxi can help you a lot. After all, it ultimately ensures a comfortable ride for the passengers without wasting any time. In case you&rsquo;ve just landed back, it&rsquo;s better to make an online airport cab booking rather than standing in those long queues.&nbsp;</p>
	<p>MakeMyTrip offers convenient and easily accessible airport cabs running all around the clock at every major airport across India. Be it a domestic or an international airport, their taxi booking services are available pretty much everywhere. So, if you&rsquo;re ready to travel to your airport without any hassle, then visit MakeMyTrip and book your airport taxi today.&nbsp;</p>
	<h2>Choose MakeMyTrip for Airport Taxi Booking</h2>
	<p>MakeMyTrip offers you a wide range of car fleets including different Hatchbacks, Sedans and SUVs for making your taxi booking to airport. Further, you have the option to pick between different models such as Swift, Indica, Honda City, Xylo and Ertiga. All the airport cabs are air-conditioned and provide plenty of leg room, making them an ideal choice for a comfortable ride. So, instead of thinking too much, just visit MakeMyTrip and make your taxi booking today!&nbsp;</p>
	<h2>Benefits of Booking an Airport Taxi on MakeMyTrip</h2>
	<p>By making an airport taxi booking on MakeMyTrip, you can enjoy dozens of travelling benefits. Below, we&rsquo;ve mentioned some of the benefits that you can avail by booking your taxi airport transfers on MakeMyTrip:&nbsp;</p>
	<h3>Safe and Secured Rides</h3>
	<p>While booking an&nbsp;airport taxi&nbsp;on MakeMyTrip, you also sign up for an entirely safe and secured ride. All the drivers of such cabs are well-trained, licensed and aware of different routes so that you can enjoy a safe and smooth&nbsp;airport pickup.&nbsp;</p>
	<h3>Cost Effectiveness</h3>
	<p>If you&rsquo;re worried about your budget, then MakeMyTrip should be your go-to platform. Here, you can easily choose and book a&nbsp;taxi from airport&nbsp;as per your budget and needs. Also, there are no hidden charges included in any transaction as MakeMyTrip believes in complete transparency.&nbsp;</p>
	<h3>Comfort At Its Best</h3>
	<p>Flight journeys can be quite long and can make you feel really tired and exhausted. Hence, standing in those long queues isn&rsquo;t an option especially when you can make a cab booking in advance and enjoy a comfortable ride home. All you&rsquo;ve to do is visit MakeMyTrip, make an advance airport taxing booking and one of the cabs will be there right outside the terminal waiting for you.&nbsp;</p>
	<h3>Steps to Book an Airport Taxi Online</h3>
	<p>Whether you&rsquo;re looking forward to booking cabs from airport or cabs to airport, MakeMyTrip&rsquo;s booking process is extremely easy. To book your airport taxi without any hassle, make sure to follow the below-mentioned steps carefully:&nbsp;</p>
	<p><strong>Step 1</strong>:&nbsp;Visit the official website of MakeMyTrip and click on the &lsquo;Cabs&rsquo; option to make a booking for your&nbsp;airport pickup and drop.</p>
	<p><strong>Step 2</strong>:&nbsp;Now, enter your ride details including your pick-up and airport drop location.</p>
	<p><strong>Step 3</strong>:&nbsp;Select one preferred cab of your choice as per your budget and number of passengers.</p>
	<p><strong>Step 4</strong>: Lastly, you can either make a partial payment for now or make a full payment to complete your booking process.&nbsp;</p>
	<p>To make the process more convenient for the passengers, MakeMyTrip accepts various payment methods such as UPI, Net Banking, Debit Cards and Credit Cards transactions. Once you&rsquo;ve booked your airport taxi, you can also check out the profile and rating of the assigned driver.</p>`
  };

  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    ...responseData,
    templateData: templateData
  };
};
export default AirportHomePage;
